/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState, useRef, useEffect } from "react";
import { TextField, Tooltip, InputAdornment, RadioGroup, FormControlLabel, Radio, MenuItem, Select, FormControl, InputLabel, Button } from "@mui/material";
import { Warning as WarningIcon, Download as DownloadIcon } from "@mui/icons-material";
import { FinancialInfoStatus } from "../../../../constants/general.constant";
import ReactSignatureCanvas from "react-signature-canvas";
import { FinancialInfoProps } from "../types";
import { downloadFinancialInfoPDF } from "../../../../utils/pdfGenerator";
import DigitalSignatureBox from "./DigitalSignatureBox";
import { openProspectKycFinancialInfoFile } from "../../../../services/prospectKyc.service";

const FinancialInfo = ({
  customerId,
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
}: FinancialInfoProps) => {
  const [financialInfoOption, setFinancialInfoOption] = useState<FinancialInfoStatus.FILE_UPLOAD | FinancialInfoStatus.FILL_FORM>(values.financialInfoOption || FinancialInfoStatus.FILL_FORM);
  const [financialInfoFileUrl, setFinancialInfoFileUrl] = useState<string | null>(null);
  const financialInfoFileUrlRef = useRef<string | null>(null);
  const sigCanvasRef = useRef<ReactSignatureCanvas | null>(null);
  const [isEditingSignature, setIsEditingSignature] = useState<boolean>(false);
  const [isPreviewingFinancialFile, setIsPreviewingFinancialFile] = useState(false);

  // Create and revoke object URL for financial info file to prevent memory leaks
  useEffect(() => {
    // Revoke previous URL if exists
    if (financialInfoFileUrlRef.current) {
      URL.revokeObjectURL(financialInfoFileUrlRef.current);
    }

    // Create new URL if file is a File object
    if (values.financialInfoFile instanceof File) {
      const objectUrl = URL.createObjectURL(values.financialInfoFile);
      financialInfoFileUrlRef.current = objectUrl;
      setFinancialInfoFileUrl(objectUrl);
    } else {
      financialInfoFileUrlRef.current = null;
      setFinancialInfoFileUrl(null);
    }

    // Cleanup on unmount
    return () => {
      if (financialInfoFileUrlRef.current) {
        URL.revokeObjectURL(financialInfoFileUrlRef.current);
      }
    };
  }, [values.financialInfoFile]);

  useEffect(() => {
    setFinancialInfoOption(values.financialInfoOption || FinancialInfoStatus.FILL_FORM);
  }, [values.financialInfoOption]);

  useEffect(() => {
    if (isEditingSignature && sigCanvasRef.current) {
      sigCanvasRef.current.clear();
      if (values.digitalSignature) {
        setTimeout(() => {
          if (sigCanvasRef.current) {
            sigCanvasRef.current.fromDataURL(values.digitalSignature);
          }
        }, 50);
      }
    }
  }, [isEditingSignature, values.digitalSignature]);

  const handleEditSignature = () => {
    setIsEditingSignature(true);
  };

  const handleSaveSignature = () => {
    if (sigCanvasRef.current) {
      const signatureData = sigCanvasRef.current.toDataURL();
      setFieldValue('digitalSignature', signatureData);
      setIsEditingSignature(false);
    }
  };

  const handleClearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const handleCancelEdit = () => {
    setIsEditingSignature(false);
  };

  const handleDownloadPDF = () => {
    downloadFinancialInfoPDF(values);
  };

  const handlePreviewFinancialFile = async () => {
    if (!customerId) {
      return;
    }

    setIsPreviewingFinancialFile(true);
    try {
      await openProspectKycFinancialInfoFile(customerId);
    } finally {
      setIsPreviewingFinancialFile(false);
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    e.target.value = value;
    handleChange(e as any);
  };

  const totalAssets = useMemo(() => {
    return (
      (parseFloat(values.cashOnHandAndBanks) || 0) +
      (parseFloat(values.savingsAccounts) || 0) +
      (parseFloat(values.iraOrOtherRetirement) || 0) +
      (parseFloat(values.accountsAndNotesReceivable) || 0) +
      (parseFloat(values.lifeInsuranceCashSurrender) || 0) +
      (parseFloat(values.stocksAndBonds) || 0) +
      (parseFloat(values.realEstate) || 0) +
      (parseFloat(values.automobileTotalPresentValue) || 0) +
      (parseFloat(values.otherPersonalProperty) || 0) +
      (parseFloat(values.otherAssets) || 0)
    );
  }, [
    values.cashOnHandAndBanks,
    values.savingsAccounts,
    values.iraOrOtherRetirement,
    values.accountsAndNotesReceivable,
    values.lifeInsuranceCashSurrender,
    values.stocksAndBonds,
    values.realEstate,
    values.automobileTotalPresentValue,
    values.otherPersonalProperty,
    values.otherAssets,
  ]);

  const totalLiabilities = useMemo(() => {
    return (
      (parseFloat(values.accountsPayable) || 0) +
      (parseFloat(values.notesPayableToBanksAndOthers) || 0) +
      (parseFloat(values.installmentAccountAuto) || 0) +
      (parseFloat(values.installmentAccountOther) || 0) +
      (parseFloat(values.loansOnLifeInsurance) || 0) +
      (parseFloat(values.mortgagesOnRealEstate) || 0) +
      (parseFloat(values.unpaidTaxes) || 0) +
      (parseFloat(values.otherLiabilities) || 0)
    );
  }, [
    values.accountsPayable,
    values.notesPayableToBanksAndOthers,
    values.installmentAccountAuto,
    values.installmentAccountOther,
    values.loansOnLifeInsurance,
    values.mortgagesOnRealEstate,
    values.unpaidTaxes,
    values.otherLiabilities,
  ]);

  const calculatedNetWorth = useMemo(() => {
    return totalAssets - totalLiabilities;
  }, [totalAssets, totalLiabilities]);

  useEffect(() => {
    setFieldValue('totalAssets', totalAssets);
    setFieldValue('totalLiabilities', totalLiabilities);
    setFieldValue('netWorth', calculatedNetWorth);
  }, [totalAssets, totalLiabilities, calculatedNetWorth]);

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg mb-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Financial Overview</h3>
          {financialInfoOption === FinancialInfoStatus.FILL_FORM && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPDF}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              Download PDF
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <TextField
              label="Personal Income *"
              name="personalIncome"
              type="text"
              value={values.personalIncome}
              onChange={handleChange}
              error={touched.personalIncome && Boolean(errors.personalIncome)}
              helperText={touched.personalIncome && errors.personalIncome}
              fullWidth
              autoComplete="off"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                style: { borderRadius: 10 }
              }}
              inputProps={{ pattern: '[0-9]*' }}
              onKeyDown={(e) => {
                const key = e.key;
                if (!/^\d$/.test(key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key) && key !== 'Meta' && key !== 'Control') {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <div>
            <TextField
              label="Business Revenue *"
              name="businessRevenue"
              type="text"
              value={values.businessRevenue}
              onChange={handleChange}
              error={touched.businessRevenue && Boolean(errors.businessRevenue)}
              helperText={touched.businessRevenue && errors.businessRevenue}
              fullWidth
              autoComplete="off"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                style: { borderRadius: 10 }
              }}
              inputProps={{ pattern: '[0-9]*' }}
              onKeyDown={(e) => {
                const key = e.key;
                if (!/^\d$/.test(key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key) && key !== 'Meta' && key !== 'Control') {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <div className="md:col-span-2">
            <TextField
              label="Source of Funds *"
              name="sourceOfFunds"
              value={values.sourceOfFunds}
              onChange={handleChange}
              error={touched.sourceOfFunds && Boolean(errors.sourceOfFunds)}
              helperText={touched.sourceOfFunds && errors.sourceOfFunds}
              fullWidth
              autoComplete="off"
              InputProps={{ style: { borderRadius: 10 } }}
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg mb-4">
        <h3 className="text-lg font-semibold mb-4">FINANCIAL STATEMENT OPTION</h3>
        <RadioGroup
          value={financialInfoOption}
          onChange={(e) => {
            const value = e.target.value as FinancialInfoStatus.FILE_UPLOAD | FinancialInfoStatus.FILL_FORM;
            setFinancialInfoOption(value);
            setFieldValue('financialInfoOption', value);
          }}
          row
        >
          <FormControlLabel value={FinancialInfoStatus.FILL_FORM} control={<Radio />} label="Fill Form" />
          <FormControlLabel value={FinancialInfoStatus.FILE_UPLOAD} control={<Radio />} label="Upload PDF" />
        </RadioGroup>
      </div>

      {financialInfoOption === FinancialInfoStatus.FILE_UPLOAD? (
        <>
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="text-lg font-semibold mb-4">Upload Financial Statement PDF</h3>
            <p className="text-sm text-red-600 mb-2 flex items-center">
              <WarningIcon fontSize="small" className="mr-1" />
              Maximum file size: 10MB
            </p>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setFieldValue('financialInfoFile', file);
                }
              }}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {touched.financialInfoFile && errors.financialInfoFile && (
              <p className="text-red-500 text-sm mt-1">{errors.financialInfoFile}</p>
            )}
            {values.financialInfoFile && (
              <div className="mt-4 text-center">
                <p className="text-sm font-medium text-gray-700 mb-2">Uploaded File:</p>
                {typeof values.financialInfoFile === 'string' ? (
                  <button
                    type="button"
                    onClick={handlePreviewFinancialFile}
                    disabled={isPreviewingFinancialFile}
                    className="text-blue-600 hover:text-blue-800 underline cursor-pointer disabled:text-gray-500 disabled:cursor-wait"
                  >
                    {isPreviewingFinancialFile ? "Previewing..." : "View Financial Statement PDF"}
                  </button>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <a
                      href={financialInfoFileUrl || ''}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      {(values.financialInfoFile as File).name}
                    </a>
                    <button
                      type="button"
                      onClick={() => setFieldValue('financialInfoFile', null)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <DigitalSignatureBox
            digitalSignature={values.digitalSignature}
            isEditingSignature={isEditingSignature}
            sigCanvasRef={sigCanvasRef}
            touched={touched}
            errors={errors}
            onEdit={handleEditSignature}
            onSave={handleSaveSignature}
            onClear={handleClearSignature}
            onCancel={handleCancelEdit}
          />
        </>
      ) : (
        <>
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="text-lg font-semibold mb-4">PERSONAL FINANCIAL STATEMENT</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <TextField
                  label="First Name"
                  name="firstName"
                  value={values.firstName}
                  onChange={handleChange}
                  error={touched.firstName && Boolean(errors.firstName)}
                  helperText={touched.firstName && errors.firstName}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Middle Name"
                  name="middleName"
                  value={values.middleName}
                  onChange={handleChange}
                  error={touched.middleName && Boolean(errors.middleName)}
                  helperText={touched.middleName && errors.middleName}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Last Name"
                  name="lastName"
                  value={values.lastName}
                  onChange={handleChange}
                  error={touched.lastName && Boolean(errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Business Phone"
                  name="businessPhone"
                  value={values.businessPhone}
                  onChange={handleChange}
                  error={touched.businessPhone && Boolean(errors.businessPhone)}
                  helperText={touched.businessPhone && errors.businessPhone}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div className="md:col-span-2">
                <TextField
                  label="Residence Address"
                  name="residenceAddress"
                  value={values.residenceAddress}
                  onChange={handleChange}
                  error={touched.residenceAddress && Boolean(errors.residenceAddress)}
                  helperText={touched.residenceAddress && errors.residenceAddress}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Residence City"
                  name="residenceCity"
                  value={values.residenceCity}
                  onChange={handleChange}
                  error={touched.residenceCity && Boolean(errors.residenceCity)}
                  helperText={touched.residenceCity && errors.residenceCity}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Residence State"
                  name="residenceState"
                  value={values.residenceState}
                  onChange={handleChange}
                  error={touched.residenceState && Boolean(errors.residenceState)}
                  helperText={touched.residenceState && errors.residenceState}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Residence Zip Code"
                  name="residenceZipCode"
                  value={values.residenceZipCode}
                  onChange={handleChange}
                  error={touched.residenceZipCode && Boolean(errors.residenceZipCode)}
                  helperText={touched.residenceZipCode && errors.residenceZipCode}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Residence Phone"
                  name="residencePhone"
                  value={values.residencePhone}
                  onChange={handleChange}
                  error={touched.residencePhone && Boolean(errors.residencePhone)}
                  helperText={touched.residencePhone && errors.residencePhone}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Business Name of Applicant/Borrower"
                  name="businessName"
                  value={values.businessName}
                  onChange={handleChange}
                  error={touched.businessName && Boolean(errors.businessName)}
                  helperText={touched.businessName && errors.businessName}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">ASSETS</h3>
              <div className="text-right">
                <p className="font-semibold">Total Assets</p>
                <p className="text-xl">${totalAssets}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <TextField
                  label="Cash on hand & in Banks"
                  name="cashOnHandAndBanks"
                  type="text"
                  value={values.cashOnHandAndBanks}
                  onChange={handleNumberChange}
                  error={touched.cashOnHandAndBanks && Boolean(errors.cashOnHandAndBanks)}
                  helperText={touched.cashOnHandAndBanks && errors.cashOnHandAndBanks}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Savings Accounts"
                  name="savingsAccounts"
                  type="text"
                  value={values.savingsAccounts}
                  onChange={handleNumberChange}
                  error={touched.savingsAccounts && Boolean(errors.savingsAccounts)}
                  helperText={touched.savingsAccounts && errors.savingsAccounts}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <Tooltip title="Complete Section 5">
                  <TextField
                    label="IRA or Other Retirement Account"
                    name="iraOrOtherRetirement"
                    type="text"
                    value={values.iraOrOtherRetirement}
                    onChange={handleNumberChange}
                    error={touched.iraOrOtherRetirement && Boolean(errors.iraOrOtherRetirement)}
                    helperText={touched.iraOrOtherRetirement && errors.iraOrOtherRetirement}
                    fullWidth
                    autoComplete="off"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      style: { borderRadius: 10 }
                    }}
                  />
                </Tooltip>
              </div>
              <div>
                <Tooltip title="Complete Section 5">
                  <TextField
                    label="Accounts & Notes Receivable"
                    name="accountsAndNotesReceivable"
                    type="text"
                    value={values.accountsAndNotesReceivable}
                    onChange={handleNumberChange}
                    error={touched.accountsAndNotesReceivable && Boolean(errors.accountsAndNotesReceivable)}
                    helperText={touched.accountsAndNotesReceivable && errors.accountsAndNotesReceivable}
                    fullWidth
                    autoComplete="off"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      style: { borderRadius: 10 }
                    }}
                  />
                </Tooltip>
              </div>
              <div>
                <Tooltip title="Complete Section 8">
                  <TextField
                    label="Life Insurance-Cash Surrender Value Only"
                    name="lifeInsuranceCashSurrender"
                    type="text"
                    value={values.lifeInsuranceCashSurrender}
                    onChange={handleNumberChange}
                    error={touched.lifeInsuranceCashSurrender && Boolean(errors.lifeInsuranceCashSurrender)}
                    helperText={touched.lifeInsuranceCashSurrender && errors.lifeInsuranceCashSurrender}
                    fullWidth
                    autoComplete="off"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      style: { borderRadius: 10 }
                    }}
                  />
                </Tooltip>
              </div>
              <div>
                <Tooltip title="Describe in Section 3">
                  <TextField
                    label="Stocks and Bonds"
                    name="stocksAndBonds"
                    type="text"
                    value={values.stocksAndBonds}
                    onChange={handleNumberChange}
                    error={touched.stocksAndBonds && Boolean(errors.stocksAndBonds)}
                    helperText={touched.stocksAndBonds && errors.stocksAndBonds}
                    fullWidth
                    autoComplete="off"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      style: { borderRadius: 10 }
                    }}
                  />
                </Tooltip>
              </div>
              <div>
                <TextField
                  label="Real Estate"
                  name="realEstate"
                  type="text"
                  value={values.realEstate}
                  onChange={handleNumberChange}
                  error={touched.realEstate && Boolean(errors.realEstate)}
                  helperText={touched.realEstate && errors.realEstate}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Automobile - Total Present Value"
                  name="automobileTotalPresentValue"
                  type="text"
                  value={values.automobileTotalPresentValue}
                  onChange={handleNumberChange}
                  error={touched.automobileTotalPresentValue && Boolean(errors.automobileTotalPresentValue)}
                  helperText={touched.automobileTotalPresentValue && errors.automobileTotalPresentValue}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Other Personal Property"
                  name="otherPersonalProperty"
                  type="text"
                  value={values.otherPersonalProperty}
                  onChange={handleNumberChange}
                  error={touched.otherPersonalProperty && Boolean(errors.otherPersonalProperty)}
                  helperText={touched.otherPersonalProperty && errors.otherPersonalProperty}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Other Assets"
                  name="otherAssets"
                  type="text"
                  value={values.otherAssets}
                  onChange={handleNumberChange}
                  error={touched.otherAssets && Boolean(errors.otherAssets)}
                  helperText={touched.otherAssets && errors.otherAssets}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">LIABILITIES</h3>
              <div className="text-right">
                <p className="font-semibold">Total Liabilities</p>
                <p className="text-xl">${totalLiabilities}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <TextField
                  label="Accounts Payable"
                  name="accountsPayable"
                  type="text"
                  value={values.accountsPayable}
                  onChange={handleNumberChange}
                  error={touched.accountsPayable && Boolean(errors.accountsPayable)}
                  helperText={touched.accountsPayable && errors.accountsPayable}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Notes Payable to Banks and Others"
                  name="notesPayableToBanksAndOthers"
                  type="text"
                  value={values.notesPayableToBanksAndOthers}
                  onChange={handleNumberChange}
                  error={touched.notesPayableToBanksAndOthers && Boolean(errors.notesPayableToBanksAndOthers)}
                  helperText={touched.notesPayableToBanksAndOthers && errors.notesPayableToBanksAndOthers}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Installment Account (Auto)"
                  name="installmentAccountAuto"
                  type="text"
                  value={values.installmentAccountAuto}
                  onChange={handleNumberChange}
                  error={touched.installmentAccountAuto && Boolean(errors.installmentAccountAuto)}
                  helperText={touched.installmentAccountAuto && errors.installmentAccountAuto}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Mo. Payments (Auto)"
                  name="moPaymentsAuto"
                  type="text"
                  value={values.moPaymentsAuto}
                  onChange={handleNumberChange}
                  error={touched.moPaymentsAuto && Boolean(errors.moPaymentsAuto)}
                  helperText={touched.moPaymentsAuto && errors.moPaymentsAuto}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Installment Account (Other)"
                  name="installmentAccountOther"
                  type="text"
                  value={values.installmentAccountOther}
                  onChange={handleChange}
                  error={touched.installmentAccountOther && Boolean(errors.installmentAccountOther)}
                  helperText={touched.installmentAccountOther && errors.installmentAccountOther}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Mo. Payments (Other)"
                  name="moPaymentsOther"
                  type="text"
                  value={values.moPaymentsOther}
                  onChange={handleChange}
                  error={touched.moPaymentsOther && Boolean(errors.moPaymentsOther)}
                  helperText={touched.moPaymentsOther && errors.moPaymentsOther}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Loans on Life Insurance"
                  name="loansOnLifeInsurance"
                  type="text"
                  value={values.loansOnLifeInsurance}
                  onChange={handleChange}
                  error={touched.loansOnLifeInsurance && Boolean(errors.loansOnLifeInsurance)}
                  helperText={touched.loansOnLifeInsurance && errors.loansOnLifeInsurance}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Mortgages on Real Estate"
                  name="mortgagesOnRealEstate"
                  type="text"
                  value={values.mortgagesOnRealEstate}
                  onChange={handleChange}
                  error={touched.mortgagesOnRealEstate && Boolean(errors.mortgagesOnRealEstate)}
                  helperText={touched.mortgagesOnRealEstate && errors.mortgagesOnRealEstate}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Unpaid Taxes"
                  name="unpaidTaxes"
                  type="text"
                  value={values.unpaidTaxes}
                  onChange={handleChange}
                  error={touched.unpaidTaxes && Boolean(errors.unpaidTaxes)}
                  helperText={touched.unpaidTaxes && errors.unpaidTaxes}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Other Liabilities"
                  name="otherLiabilities"
                  type="text"
                  value={values.otherLiabilities}
                  onChange={handleChange}
                  error={touched.otherLiabilities && Boolean(errors.otherLiabilities)}
                  helperText={touched.otherLiabilities && errors.otherLiabilities}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg mb-4 border-2 border-blue-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-blue-900">NET WORTH</h3>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-700">${calculatedNetWorth.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="text-lg font-semibold mb-4">CONTINGENT LIABILITIES</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <TextField
                  label="As Endorser or Co-Maker"
                  name="asEndorserOrCoMaker"
                  value={values.asEndorserOrCoMaker}
                  onChange={handleChange}
                  error={touched.asEndorserOrCoMaker && Boolean(errors.asEndorserOrCoMaker)}
                  helperText={touched.asEndorserOrCoMaker && errors.asEndorserOrCoMaker}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Legal Claims & Judgments"
                  name="legalClaimsAndJudgments"
                  value={values.legalClaimsAndJudgments}
                  onChange={handleChange}
                  error={touched.legalClaimsAndJudgments && Boolean(errors.legalClaimsAndJudgments)}
                  helperText={touched.legalClaimsAndJudgments && errors.legalClaimsAndJudgments}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Provision for Federal Income Tax"
                  name="provisionForFederalIncomeTax"
                  value={values.provisionForFederalIncomeTax}
                  onChange={handleChange}
                  error={touched.provisionForFederalIncomeTax && Boolean(errors.provisionForFederalIncomeTax)}
                  helperText={touched.provisionForFederalIncomeTax && errors.provisionForFederalIncomeTax}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Other Special Debt"
                  name="otherSpecialDebt"
                  value={values.otherSpecialDebt}
                  onChange={handleChange}
                  error={touched.otherSpecialDebt && Boolean(errors.otherSpecialDebt)}
                  helperText={touched.otherSpecialDebt && errors.otherSpecialDebt}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="text-lg font-semibold mb-4">Section 1. Source of Income</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <TextField
                  label="Salary"
                  name="salary"
                  type="text"
                  value={values.salary}
                  onChange={handleChange}
                  error={touched.salary && Boolean(errors.salary)}
                  helperText={touched.salary && errors.salary}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Net Investment Income"
                  name="netInvestmentIncome"
                  type="text"
                  value={values.netInvestmentIncome}
                  onChange={handleChange}
                  error={touched.netInvestmentIncome && Boolean(errors.netInvestmentIncome)}
                  helperText={touched.netInvestmentIncome && errors.netInvestmentIncome}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <TextField
                  label="Real Estate Income"
                  name="realEstateIncome"
                  type="text"
                  value={values.realEstateIncome}
                  onChange={handleChange}
                  error={touched.realEstateIncome && Boolean(errors.realEstateIncome)}
                  helperText={touched.realEstateIncome && errors.realEstateIncome}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div className="md:col-span-2">
                <TextField
                  label="Other Income (Describe Below)"
                  name="otherIncome"
                  type="text"
                  value={values.otherIncome}
                  onChange={handleChange}
                  error={touched.otherIncome && Boolean(errors.otherIncome)}
                  helperText={touched.otherIncome && errors.otherIncome}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="text-lg font-semibold mb-4">Description of Other Income in Section 1</h3>
            <div>
              <TextField
                label="Description"
                name="descriptionOfOtherIncome"
                value={values.descriptionOfOtherIncome}
                onChange={handleChange}
                error={touched.descriptionOfOtherIncome && Boolean(errors.descriptionOfOtherIncome)}
                helperText={touched.descriptionOfOtherIncome && errors.descriptionOfOtherIncome}
                fullWidth
                autoComplete="off"
                multiline
                rows={4}
                InputProps={{ style: { borderRadius: 10 } }}
              />
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="text-lg font-semibold mb-4">Joint Person Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <TextField
                  label="Personal Email"
                  name="jointPersonalInfo.email"
                  type="email"
                  value={values.jointPersonalInfo?.email || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="SSN"
                  name="jointPersonalInfo.ssn"
                  value={values.jointPersonalInfo?.ssn || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Date of Birth"
                  name="jointPersonalInfo.dateOfBirth"
                  type="date"
                  value={values.jointPersonalInfo?.dateOfBirth || ""}
                  onChange={(e) => {
                    const dateValue = e.target.value;
                    if (dateValue) {
                     const enteredDate = new Date(dateValue);
                     const maxFutureDate = new Date();
                     maxFutureDate.setFullYear(
                       maxFutureDate.getFullYear() + 50,
                     );
                     if (enteredDate > maxFutureDate) {
                       return;
                     }
                    }
                    handleChange(e);
                  }}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ style: { borderRadius: 10 } }}
                  placeholder="MM/DD/YYYY"
                  inputProps={{
                    max: new Date(new Date().getFullYear() + 50, 11, 31).toISOString().split("T")[0],
                  }}
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="text-lg font-semibold mb-4">Joint Person Income</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <TextField
                  label="Annual Income"
                  name="jointPersonalIncome.annualIncome"
                  type="text"
                  value={values.jointPersonalIncome?.annualIncome || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { borderRadius: 10 }
                  }}
                />
              </div>
              <div>
                <FormControl fullWidth>
                  <InputLabel style={{ borderRadius: 10 }}>Employment Status</InputLabel>
                  <Select
                    name="jointPersonalIncome.employmentStatus"
                    value={values.jointPersonalIncome?.employmentStatus || ""}
                    onChange={handleChange as any}
                    label="Employment Status"
                    style={{ borderRadius: 10 }}
                  >
                    <MenuItem value="Employed">Employed</MenuItem>
                    <MenuItem value="Self-Employed">Self-Employed</MenuItem>
                    <MenuItem value="Retired">Retired</MenuItem>
                    <MenuItem value="Unemployed">Unemployed</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div>
                <TextField
                  label="Employer First Name"
                  name="jointPersonalIncome.employerFirstName"
                  value={values.jointPersonalIncome?.employerFirstName || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Employer Middle Name"
                  name="jointPersonalIncome.employerMiddleName"
                  value={values.jointPersonalIncome?.employerMiddleName || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Employer Last Name"
                  name="jointPersonalIncome.employerLastName"
                  value={values.jointPersonalIncome?.employerLastName || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Employer Phone"
                  name="jointPersonalIncome.employerPhone"
                  value={values.jointPersonalIncome?.employerPhone || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Job Title"
                  name="jointPersonalIncome.jobTitle"
                  value={values.jointPersonalIncome?.jobTitle || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Years Employed"
                  name="jointPersonalIncome.yearsEmployed"
                  type="text"
                  value={values.jointPersonalIncome?.yearsEmployed || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="text-lg font-semibold mb-4">Joint Account Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <TextField
                  label="Joint Account First Name"
                  name="jointAccount.firstName"
                  value={values.jointAccount?.firstName || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Joint Account Middle Name"
                  name="jointAccount.middleName"
                  value={values.jointAccount?.middleName || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Joint Account Last Name"
                  name="jointAccount.lastName"
                  value={values.jointAccount?.lastName || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Joint Account Phone"
                  name="jointAccount.phone"
                  value={values.jointAccount?.phone || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div className="md:col-span-2">
                <TextField
                  label="Joint Account Address"
                  name="jointAccount.address"
                  value={values.jointAccount?.address || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Joint Account City"
                  name="jointAccount.city"
                  value={values.jointAccount?.city || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Joint Account State"
                  name="jointAccount.state"
                  value={values.jointAccount?.state || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
              <div>
                <TextField
                  label="Joint Account Zip Code"
                  name="jointAccount.zipCode"
                  value={values.jointAccount?.zipCode || ""}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: 10 } }}
                />
              </div>
            </div>
          </div>

          <DigitalSignatureBox
            digitalSignature={values.digitalSignature}
            isEditingSignature={isEditingSignature}
            sigCanvasRef={sigCanvasRef}
            touched={touched}
            errors={errors}
            onEdit={handleEditSignature}
            onSave={handleSaveSignature}
            onClear={handleClearSignature}
            onCancel={handleCancelEdit}
          />
        </>
      )}
    </div>
  );
};

export default FinancialInfo;
